import type { TFunction } from 'i18next';
import { shortOperatorLabel } from './operator-stamp';
import type { DisruptionDetail, DisruptionTriggerSource } from './types';

// Who declared a disruption — ONE reading of `openedBy@v1` for every surface that says it
// (the classic header, the operar header, the first row of «Quién hizo qué»). The name
// travels on the disruption itself (stamped at declare time); without one the short URN
// stands in, and an open nobody was recorded for says so rather than naming anyone.

export type OpenerKind =
  /** An operator declared it (`openedBy` present). */
  | 'person'
  /** Nexa opened it on a signal (airline status, weather, prediction). */
  | 'auto'
  /** Declared from a manifest file — the commit runs in the airline domain, which does
   *  not forward who committed it yet. */
  | 'file'
  /** Declared by hand, but before attribution existed (or by a caller that named nobody). */
  | 'unknown';

export interface IOpener {
  kind: OpenerKind;
  urn?: string;
  name?: string;
  source?: DisruptionTriggerSource;
}

const AUTO_SOURCES: ReadonlySet<DisruptionTriggerSource> = new Set(['AUTO_WEATHER', 'AUTO_AIRLINE', 'AUTO_PREDICTOR']);

export function openerOf(
  detail: Pick<DisruptionDetail, 'openedBy' | 'openedByName' | 'triggerSource' | 'provenance'>,
): IOpener {
  const source = detail.triggerSource;
  if (detail.openedBy) {
    return {
      kind: 'person',
      urn: detail.openedBy,
      ...(detail.openedByName ? { name: detail.openedByName } : {}),
      ...(source ? { source } : {}),
    };
  }
  if (source && AUTO_SOURCES.has(source)) return { kind: 'auto', source };
  if (source === 'MANUAL_FILE' || detail.provenance?.kind === 'FILE_IMPORT') return { kind: 'file', ...(source ? { source } : {}) };
  return { kind: 'unknown', ...(source ? { source } : {}) };
}

/** «Declarada por Ana Pérez» / «Detectada automáticamente (aviso de la aerolínea)» / … */
export function openerLine(opener: IOpener, t: TFunction<'disruptions'>): string {
  switch (opener.kind) {
    case 'person':
      return t('opener.person', { who: opener.name ?? shortOperatorLabel(opener.urn ?? '') });
    case 'auto':
      return t('opener.auto', { source: t(`opener.source.${opener.source ?? 'AUTO_AIRLINE'}`) });
    case 'file':
      return t('opener.file');
    default:
      return t('opener.unknown');
  }
}
